import { useState } from 'react'
import { motion } from 'framer-motion'
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef, getSortedRowModel, type SortingState } from '@tanstack/react-table'
import { ArrowUpDown, Trophy, Users, Calendar } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useTeams } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type { Team, Division } from '@/types/api'
import { SeasonChart } from './SeasonChart'

const columns: ColumnDef<Team>[] = [
  { accessorKey: 'age', header: 'Age' },
  { accessorKey: 'coach', header: 'Coach' },
  { accessorKey: 'players', header: 'Players' },
  { accessorKey: 'wins', header: 'W' },
  { accessorKey: 'losses', header: 'L' },
  { accessorKey: 'draws', header: 'D' },
  { accessorKey: 'training', header: 'Training' },
]

export function TeamsTabs() {
  const { data: teams } = useTeams()
  const [division, setDivision] = useState<Division>('boys')
  const [selectedId, setSelectedId] = useState<string>('b-14')

  if (!teams) return <div className="h-64" />

  const ageList = teams[division]
  const selected = ageList.find((t) => t.id === selectedId) ?? ageList[0]

  return (
    <div>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Tabs
          value={division}
          onValueChange={(v) => {
            setDivision(v as Division)
            setSelectedId(v === 'boys' ? 'b-14' : 'g-14')
          }}
        >
          <TabsList>
            <TabsTrigger value="boys">Boys</TabsTrigger>
            <TabsTrigger value="girls">Girls</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {ageList.map((t) => (
          <button
            key={t.id}
            onClick={() => setSelectedId(t.id)}
            className={cn(
              'rounded-md border px-4 py-2 font-display text-sm uppercase tracking-wide transition-colors',
              selectedId === t.id
                ? 'bg-cosmos-green text-white border-cosmos-green'
                : 'bg-card border-border hover:border-cosmos-green hover:text-cosmos-green'
            )}
          >
            {t.age}
          </button>
        ))}
      </div>

      <motion.div
        key={selected.id}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]"
      >
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="font-display text-3xl md:text-4xl uppercase">
                {division === 'boys' ? 'Boys' : 'Girls'} {selected.age}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">Head Coach · {selected.coach}</p>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Next Match</div>
              <div className="text-sm font-semibold">{selected.nextMatch}</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <StatChip icon={<Users className="h-4 w-4" />} label="Roster" value={String(selected.players)} />
            <StatChip icon={<Trophy className="h-4 w-4" />} label="Wins" value={String(selected.wins)} />
            <StatChip icon={<Calendar className="h-4 w-4" />} label="Training" value={selected.training} small />
          </div>

          <SeasonChart teamId={selected.id} />
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="p-4 border-b border-border">
            <div className="font-display text-lg uppercase tracking-wider">{division === 'boys' ? 'Boys' : 'Girls'} Rosters</div>
            <div className="text-xs text-muted-foreground">Sortable — click any column header.</div>
          </div>
          <TeamsTable data={ageList} onRowClick={(t) => setSelectedId(t.id)} selectedId={selectedId} />
        </div>
      </motion.div>
    </div>
  )
}

function StatChip({ icon, label, value, small }: { icon: React.ReactNode; label: string; value: string; small?: boolean }) {
  return (
    <div className="rounded-lg bg-muted/50 p-3">
      <div className="flex items-center gap-2 text-cosmos-green mb-1">{icon}<span className="text-[10px] uppercase tracking-widest">{label}</span></div>
      <div className={cn('font-display num', small ? 'text-sm' : 'text-2xl')}>{value}</div>
    </div>
  )
}

function TeamsTable({ data, onRowClick, selectedId }: { data: Team[]; onRowClick: (t: Team) => void; selectedId: string }) {
  const [sorting, setSorting] = useState<SortingState>([])
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })
  return (
    <div className="max-h-[420px] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 sticky top-0">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th key={h.id} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  <button
                    onClick={h.column.getToggleSortingHandler()}
                    className="inline-flex items-center gap-1 hover:text-cosmos-green"
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                    <ArrowUpDown className="h-3 w-3 opacity-50" />
                  </button>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onRowClick(row.original)}
              className={cn(
                'cursor-pointer border-t border-border/50 hover:bg-cosmos-green/8',
                row.original.id === selectedId && 'bg-cosmos-green/10'
              )}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-3 py-2 num">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
